import React from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { MessageCircle, MapPin, ArrowUpRight, Clock } from "lucide-react";
import { Amenity, UnitMeta } from "@/components/Amenities";
import { LOCATIONS, MESSENGER_URL } from "@/data/staycation";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { key: "", label: "All Locations" },
  ...LOCATIONS.map((l) => ({ key: l.key, label: l.name })),
];

const TYPES = [
  { key: "", label: "Any Type" },
  { key: "Studio", label: "Studio" },
  { key: "1-Bedroom", label: "1-Bedroom" },
  { key: "2-Bedroom", label: "2-Bedroom" },
];

const FALLBACK_IMG = "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1200&q=80&auto=format&fit=crop";

function UnitCard({ unit }) {
  const messengerLink = `${MESSENGER_URL}?ref=unit_${unit.id}`;
  const handleImgError = (e) => {
    if (e.currentTarget.src !== FALLBACK_IMG) {
      e.currentTarget.src = FALLBACK_IMG;
    }
  };
  return (
    <article className="xs-card group" data-testid={`unit-card-${unit.id}`}>
      <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100">
        <img
          src={unit.image}
          alt={unit.name}
          width="600"
          height="450"
          loading="eager"
          decoding="async"
          fetchPriority="high"
          onError={handleImgError}
          data-testid={`unit-image-${unit.id}`}
          className="xs-img absolute inset-0 h-full w-full object-cover"
        />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-white/95 backdrop-blur px-2.5 py-1 text-[11px] font-semibold text-neutral-800">
          <MapPin className="h-3 w-3 text-[var(--xs-primary)]" /> {unit.near}
        </span>
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-lg font-bold leading-tight">{unit.name}</h3>
            <p className="text-xs text-neutral-500 mt-0.5">{unit.location_name}</p>
          </div>
          <div className="text-right shrink-0">
            {unit.rate_max_php && unit.rate_max_php > unit.rate_php ? (
              <>
                <p className="font-display text-lg font-bold text-[var(--xs-primary)] leading-tight">
                  ₱{unit.rate_php.toLocaleString()} – ₱{unit.rate_max_php.toLocaleString()}
                </p>
                <p className="text-[11px] text-neutral-500">/ night</p>
              </>
            ) : (
              <>
                <p className="font-display text-lg font-bold text-[var(--xs-primary)]">₱{unit.rate_php.toLocaleString()}</p>
                <p className="text-[11px] text-neutral-500">/ night</p>
              </>
            )}
          </div>
        </div>
        <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{unit.highlight}</p>
        <div className="mt-4">
          <UnitMeta unit={unit} />
        </div>
        <div className="mt-4 flex flex-wrap gap-x-3 gap-y-1.5">
          {unit.amenities.map((a) => (
            <Amenity key={a} k={a} />
          ))}
        </div>
        {unit.check_in && (
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[var(--xs-bg-alt)] px-3 py-1.5 text-xs text-neutral-700" data-testid={`unit-times-${unit.id}`}>
            <Clock className="h-3.5 w-3.5 text-[var(--xs-primary)]" />
            <span><strong>Check-in</strong> {unit.check_in} · <strong>Check-out</strong> {unit.check_out}</span>
          </div>
        )}
        <div className="mt-5 flex items-center gap-2">
          <a
            href={messengerLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`unit-book-${unit.id}`}
            className="xs-btn-primary inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-xs font-semibold"
          >
            <MessageCircle className="h-3.5 w-3.5" /> Book via Messenger
          </a>
          <Link
            to={`/booking?unit=${unit.id}`}
            className="inline-flex items-center gap-1 rounded-full px-4 py-2.5 text-xs font-semibold text-neutral-800 hover:text-[var(--xs-primary)]"
            data-testid={`unit-details-${unit.id}`}
          >
            Check Availability <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function Units() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = searchParams.get("location") || "";
  const unitType = searchParams.get("type") || "";
  const [units, setUnits] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    const params = {};
    if (location) params.location = location;
    if (unitType) params.unit_type = unitType;
    axios
      .get(`${API}/units`, { params })
      .then((r) => {
        if (active) setUnits(r.data);
      })
      .catch((e) => {
        if (active) setError(e?.message || "Failed to load units");
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [location, unitType]);

  const setParam = (key, value) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true }
    );
  };

  return (
    <div data-testid="units-page">
      <Seo
        title="Available Units — 30 cozy condo staycations across Pasay & Las Piñas"
        description="Browse 30 staycation units at Shell, Shore 1, Shore 2 & South Residences. Studios, 1-BR & 2-BR — from ₱1,750/night. Pool views, walking distance to SM Mall of Asia & SM Southmall."
        path="/units"
      />
      <section className="mx-auto max-w-7xl px-5 lg:px-8 pt-14 pb-8">
        <div>
          <p className="xs-eyebrow">Available Units</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-2 max-w-2xl">Find your cozy corner of the city.</h1>
          <p className="mt-4 text-neutral-600 max-w-xl">Browse our 30 hand-cared-for units across four prime addresses. Tap any unit to book via Messenger.</p>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-2" data-testid="filter-location">
            {FILTERS.map((f) => (
              <button
                key={f.key || "all"}
                onClick={() => setParam("location", f.key)}
                data-testid={`filter-loc-${f.key || "all"}`}
                className={`rounded-full px-4 py-2 text-xs font-semibold border transition-colors ${
                  location === f.key
                    ? "bg-[var(--xs-primary)] text-white border-transparent"
                    : "bg-white text-neutral-700 border-neutral-200 hover:border-[var(--xs-primary)]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="h-5 w-px bg-neutral-200 mx-1 hidden sm:block" />
          <div className="flex flex-wrap gap-2" data-testid="filter-type">
            {TYPES.map((t) => (
              <button
                key={t.key || "any"}
                onClick={() => setParam("type", t.key)}
                data-testid={`filter-type-${t.key || "any"}`}
                className={`rounded-full px-4 py-2 text-xs font-semibold border transition-colors ${
                  unitType === t.key
                    ? "bg-neutral-900 text-white border-transparent"
                    : "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-900"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-20">
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="xs-card animate-pulse">
                <div className="aspect-[4/3] bg-neutral-100" />
                <div className="p-5 space-y-3">
                  <div className="h-4 w-2/3 bg-neutral-100 rounded" />
                  <div className="h-3 w-full bg-neutral-100 rounded" />
                  <div className="h-3 w-1/2 bg-neutral-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        )}
        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700" data-testid="units-error">
            Could not load units: {error}
          </div>
        )}
        {!loading && !error && units.length === 0 && (
          <div className="text-center py-20 text-neutral-500" data-testid="units-empty">
            No units found for this filter. Try another location.
          </div>
        )}
        {!loading && !error && units.length > 0 && (
          <GroupedUnits units={units} />
        )}
      </section>
    </div>
  );
}

function GroupedUnits({ units }) {
  // Group by location_key in canonical order
  const LOCATION_ORDER = [
    { key: "shell-residences", title: "Shell Residences", subtitle: "Pasay City · Near SM Mall of Asia" },
    { key: "shore-1", title: "Shore 1 Residences", subtitle: "Pasay City · Near SM Mall of Asia" },
    { key: "shore-2", title: "Shore 2 Residences", subtitle: "Pasay City · Near SM Mall of Asia" },
    { key: "south-residences", title: "South Residences", subtitle: "Las Piñas · Near SM Southmall" },
  ];

  const groups = LOCATION_ORDER
    .map((loc) => ({
      ...loc,
      items: units.filter((u) => u.location_key === loc.key),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="space-y-16" data-testid="units-grid">
      {groups.map((g) => (
        <section key={g.key} data-testid={`group-${g.key}`}>
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <p className="xs-eyebrow">{g.subtitle}</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">
                {g.title}
                <span className="ml-3 text-base font-medium text-neutral-400">
                  {g.items.length} unit{g.items.length !== 1 ? "s" : ""}
                </span>
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {g.items.map((u) => (
              <UnitCard key={u.id} unit={u} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
